// src/lib/liff.js
// LINEログイン（LIFF ディープリンク方式）の土台。
// 実装は Core(~/suomi-admin) の src/lib/liffQuery.js を移植したもの。あちらの会員証ミニアプリで
// 実機で踏んだ罠（トークン失効・liff.login 禁止・liff.state・コールドスタート）が全部入っている。
// ⚠ここのコメントは実機で痛い目を見た結果なので、消さないこと。
//
// 方式の選択（2026-10-01）: Firebase Auth の OIDC プロバイダ(oidc.line)ではなく LIFF にした。
//   - LIFF はスマホの LINE アプリのログイン状態をそのまま使えるので、LINE のメールアドレス+
//     パスワード入力が発生しない（覚えている人がほとんどいない）。groom 予約で実績あり。
//   - Firebase の signInWithRedirect は authDomain が別ドメイン(*.firebaseapp.com)になるため、
//     サードパーティ Cookie を切る iOS Safari で詰まるリスクがある。custom token にはその問題が無い。

const LIFF_SDK = "https://static.line-scdn.net/liff/edge/2/sdk.js";

/** 環境に焼いた LIFF ID（審査用と本番用で複数持てるようカンマ区切りを許す）。 */
export function liffIdCandidates() {
  const raw = process.env.REACT_APP_LINE_LIFF_ID || "";
  const all = raw.split(",").map((v) => v.trim()).filter(Boolean);

  // 前回通ったIDを先頭に（外れを先に試すと init が2回走って表示が遅れる）
  let known = null;
  try {
    known = localStorage.getItem("spaceosLiffIdOk");
  } catch (_e) {
    /* noop */
  }
  return known && all.includes(known) ? [known, ...all.filter((v) => v !== known)] : all;
}

export function isLineLoginEnabled() {
  return liffIdCandidates().length > 0;
}

/**
 * liff.line.me 経由のディープリンクURL。スマホではユニバーサルリンクで LINE アプリが起動し、
 * アプリのログイン状態がそのまま使われる（Webログインのパスワード入力が不要になる）。
 * アプリ未導入/PCは LINE の Web ログインへ自然にフォールバックする。
 * ⚠LINEアプリ内ブラウザは外部ブラウザとストレージが別。復帰先などの状態は URL で運ぶこと
 *   （sessionStorage の pendingIntent は LIFF 側に届かない）。
 */
export function buildLineLoginUrl(params = {}) {
  const [liffId] = liffIdCandidates();
  if (!liffId) return null;

  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  ).toString();

  return `https://liff.line.me/${liffId}${q ? `?${q}` : ""}`;
}

/**
 * liff.line.me/{liffId}?query 経由で開くと、endpoint には query がそのまま来ず
 * `?liff.state=<エンコード済みの query>` に包まれて届く（liff.init() が後で本来のURLへ
 * replace する）。ページは init 前にパラメータを読むので、liff.state があれば自前で剥がす。
 */
export function liffAwareQuery() {
  const raw = new URLSearchParams(window.location.search);
  const state = raw.get("liff.state");
  if (!state) return raw;

  const q = state.includes("?") ? state.slice(state.indexOf("?") + 1) : state;
  const unwrapped = new URLSearchParams(q);
  for (const [k, v] of raw.entries()) {
    if (k !== "liff.state" && !unwrapped.has(k)) unwrapped.set(k, v);
  }
  return unwrapped;
}

/**
 * LINEログイン後にURLへ付く認可パラメータ(code/state等)を消す。
 * ⚠自前でリロードする前に必ず呼ぶこと。残したままリロードすると同じ認可コードを二度使うことになり、
 *   liff.init が "invalid authorization code" で落ちる。liff.init 前に呼んではいけない。
 */
export function stripAuthParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    let touched = false;
    for (const k of [
      "code", "state", "liff.state", "liffClientId", "liffRedirectUri",
      "error", "errorCode", "error_description",
    ]) {
      if (p.has(k)) { p.delete(k); touched = true; }
    }
    if (!touched) return false;
    const q = p.toString();
    window.history.replaceState(null, "", window.location.pathname + (q ? "?" + q : ""));
    return true;
  } catch (_e) {
    return false;
  }
}

/**
 * liff.state の後始末をSDKが終える（URLから消える）まで少し待つ。
 * 待ち続けると、後から来る SDK のリロードが通信中のAPIコールを殺すので、
 * 時間切れなら自分でURLを書き換えて競走を終わらせる（ここでは絶対に止まらない）。
 */
export async function waitLiffStateSettled(maxMs = 1500) {
  const t0 = Date.now();
  while (Date.now() - t0 < maxMs) {
    if (!new URLSearchParams(window.location.search).has("liff.state")) return;
    await new Promise((r) => setTimeout(r, 100));
  }
  const raw = new URLSearchParams(window.location.search);
  const state = raw.get("liff.state");
  if (state) {
    const q = state.includes("?") ? state.slice(state.indexOf("?") + 1) : state;
    const merged = new URLSearchParams(q);
    for (const [k, v] of raw.entries()) {
      if (k !== "liff.state" && !merged.has(k)) merged.set(k, v);
    }
    window.history.replaceState(null, "", `${window.location.pathname}?${merged.toString()}`);
  }
}

/**
 * LIFFブラウザの決定的な罠への先回り: アクセストークンは「LIFFを閉じると失効」するが、
 * SDK は失効済みトークンをキャッシュから返す。つまり新しいセッションの最初の1回はほぼ必ず
 * 401 になる。失敗してから直すのではなく、古いトークンを検出したら先に logout→リロードして
 * init の自動ログインに再発行させる。
 * sessionStorage はリロードを跨いで生き、LIFFを閉じると消えるので判定にそのまま使える。
 * @returns true = リロードへ遷移した（呼び出し側は処理を打ち切る）
 */
export function ensureFreshLiffToken(key) {
  if (!(window.liff?.isInClient && window.liff.isInClient())) return false; // 外部ブラウザは失効しない

  const fresh = `liff-token-fresh:${key}`;
  const guard = `liff-relogin:${key}`;

  if (sessionStorage.getItem(fresh)) return false;
  if (sessionStorage.getItem(guard)) {
    // リロード直後(2回目のロード): init の自動ログインが新しいトークンを発行済み
    sessionStorage.setItem(fresh, "1");
    sessionStorage.removeItem(guard);
    return false;
  }

  sessionStorage.setItem(guard, "1");
  try {
    window.liff.logout();
  } catch (_e) {
    /* noop */
  }
  stripAuthParams();
  window.location.reload();
  return true;
}

/**
 * 未ログイン時のログイン導線。
 * ⚠LIFFブラウザ(LINEアプリ内)で liff.login() を呼ぶのは LINE 公式の禁止パターンで、
 *   リダイレクトが宙吊りになり**無限ローディング**になる。LINEアプリ内でここに来るのは
 *   「開いた LIFF と init した LIFF のチャネルが違う」時なので、黙って詰まらせずエラーにする。
 * @returns true = ログインへ遷移した（呼び出し側は処理を打ち切る）
 */
export function loginOrThrow() {
  if (window.liff?.isInClient && window.liff.isInClient()) {
    throw new Error("この画面を開けませんでした。お手数ですが、開き直してください。");
  }
  window.liff.login();
  return true;
}

/**
 * 止まらないための時間切れ。LIFF の init / SDK 読み込みは、LINE の webview の状態が
 * おかしいと**解決も失敗もしない**ことがある（＝ローディングのまま固まる）。
 * 固まるくらいなら、理由の言えるエラーにして「開き直す」導線へ出す。
 */
function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_res, rej) =>
      setTimeout(() => rej(new Error(`TIMEOUT:${label}`)), ms)
    ),
  ]);
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (window.liff) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("LINEとの通信に失敗しました。通信状況をご確認ください。"));
    document.body.appendChild(s);
  });
}

/**
 * LIFF を初期化して LINE アクセストークンを取る。
 * @param {{devUserId?: string, key?: string}} opts
 *   devUserId: `?dev=Uxxxx` 指定時のバイパス（サーバー側 ALLOW_DEV_LINE_TOKEN=true のときだけ通る）
 * @returns {Promise<{accessToken:string, displayName:string}|null>} null = 画面遷移したので中断
 */
export async function acquireLineAccessToken({
  devUserId = "",
  key = "spaceos-login",
  onStage = () => {},
} = {}) {
  if (devUserId) {
    // ⚠デプロイ済みの dev では通らない。Core の .env.suomin-9ff5a は ALLOW_DEV_LINE_TOKEN を
    //   意図的に未設定にしている（dev にも実顧客の person/残高が入っているため成りすまし穴を塞ぐ）。
    //   使えるのはローカルで functions/.env.local に置いたときだけ。
    return { accessToken: `dev:${devUserId}`, displayName: "" };
  }

  const candidates = liffIdCandidates();
  if (candidates.length === 0) {
    throw new Error("LINEログインは現在ご利用いただけません。");
  }

  onStage("sdk");
  await withTimeout(loadScript(LIFF_SDK), 15000, "sdk");

  // 審査用と本番用は別IDで同一エンドポイントを指すため、当たるまで順に試す
  onStage("init");
  let initErr = null;
  let initialized = false;
  for (const id of candidates) {
    try {
      await withTimeout(window.liff.init({ liffId: id }), 12000, "init");
      try { localStorage.setItem("spaceosLiffIdOk", id); } catch (_e) { /* noop */ }
      initialized = true;
      break;
    } catch (e) {
      initErr = e;
    }
  }
  if (!initialized) throw initErr || new Error("LINEログインの初期化に失敗しました。");

  onStage("state");
  await waitLiffStateSettled();

  // 新しいセッションのキャッシュ済みトークンは失効している(LINE仕様)。先に取り直す。
  // ⚠isLoggedIn の判定より**先**に行う。logout 直後のリロードでは isLoggedIn が false に
  //   なることがあり、先に判定すると「LINEアプリ内なのにログインできない」エラーへ落ちる。
  onStage("token");
  if (ensureFreshLiffToken(key)) return null;

  if (!window.liff.isLoggedIn()) {
    if (loginOrThrow()) return null;
  }

  const accessToken = window.liff.getAccessToken() || "";
  if (!accessToken) throw new Error("LINEの認証情報を取得できませんでした。");

  onStage("profile");
  let displayName = "";
  try {
    displayName = (await withTimeout(window.liff.getProfile(), 8000, "profile"))?.displayName || "";
  } catch (_e) {
    /* noop: 表示名は無くても進める */
  }

  return { accessToken, displayName };
}
